/**
 * Shared rendering helpers for profit baseline rows: data-quality flag chips and
 * the admin-table cell styling (matching SchedulerAdminPage / ReconciliationPage).
 */

import React from 'react';
import { Chip, Stack, Tooltip } from '@mui/material';
import type { ProfitFlag } from '@techgitter/contracts';

export const headCellSx = {
  backgroundColor: 'rgba(0, 0, 0, 0.08)',
  color: 'rgba(0,0,0,0.8)',
  fontSize: '0.75rem',
  fontWeight: 'bold',
  textTransform: 'uppercase' as const,
};
export const bodyCellSx = { fontSize: '0.8rem' };

const FLAG_META: Record<ProfitFlag, { label: string; color: 'warning' | 'error' | 'info'; tip: string }> = {
  unknownBuy: { label: 'No buy price', color: 'error', tip: 'No catalogue price and no recorded cost — profit can’t be computed.' },
  ambiguousBuy: { label: 'Buy price assumed', color: 'info', tip: 'The offer name + cadence matched several Synnex catalogue prices (e.g. annual-commitment vs month-to-month); the cheapest was taken. Override it in the simulator if needed.' },
  unknownSell: { label: 'No billed line', color: 'warning', tip: 'A subscription we pay for with no matching customer billing line.' },
  noSubscription: { label: 'No subscription', color: 'warning', tip: 'A billed line with no active subscription for this SKU.' },
  seatMismatch: { label: 'Seat mismatch', color: 'info', tip: 'Billed seats differ from committed subscription seats.' },
  mixedCadence: { label: 'Mixed cadence', color: 'info', tip: 'Buy / sell / subscription cadences disagree — one side was normalized ÷12.' },
  cancelledOnly: { label: 'Cancelled only', color: 'warning', tip: 'The only subscription for this SKU is cancelled.' },
};

export const ProfitFlagChips: React.FC<{ flags: ProfitFlag[] }> = ({ flags }) => {
  if (!flags.length) return <>—</>;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {flags.map(f => {
        const meta = FLAG_META[f];
        return (
          <Tooltip key={f} title={meta?.tip ?? f}>
            <Chip size="small" color={meta?.color ?? 'default'} label={meta?.label ?? f} sx={{ fontSize: '0.65rem' }} />
          </Tooltip>
        );
      })}
    </Stack>
  );
};
